// Single source of truth for the Tools section.
// Used by ToolsHome (dashboard) and — eventually — AppSidebar so labels and
// permissions live in one place.
//
// Each entry:
//   id           — slug used in the URL (/tools/<id>) and for permission checks
//   name         — display label on the card
//   description  — short blurb (~12 words) shown on the card
//   Icon         — Lucide component, used as the ghost watermark on the card
//   accent       — { hue: hsl-h, name: tailwind-ish token } drives the per-card
//                  border sweep, ring glow, and ghost-icon tint on hover
//   image        — /public path; null falls back to a tinted gradient
//   group        — id of a TOOL_GROUPS section (the Tools rail folds tools
//                  into these); `pinned: true` instead puts a tool above every
//                  section (Ask Clara AI — the flagship)

import {
  Crown, Hash, Dice5, Rocket, Gift, Calculator, Send, Zap, Globe, Clock, Archive, ArrowLeftRight, Gauge, Cake, Ticket, Sparkles, Flag, Undo2, ShieldAlert,
} from 'lucide-react';

const TOOLS = [
  {
    id: 'clara-ai',
    pinned: true,
    name: 'Ask Clara AI',
    description: 'Procedures, live account checks, and drafted customer replies — conversationally.',
    Icon: Sparkles,
    accent: { from: '#34d0c0', to: '#0b7a6d', glow: 'rgba(20, 184, 166, 0.45)' },
    image: '/tool-cards/clara-ai.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
  {
    id: 'vip-calculator',
    group: 'account',
    name: 'VIP Progress',
    description: 'Calculate VIP flag progress and generate per-market customer messages.',
    Icon: Crown,
    accent: { from: '#f5c542', to: '#b8860b', glow: 'rgba(245, 197, 66, 0.45)' },
    image: '/tool-cards/vip-calculator.webp',
    supportedMarkets: ['com', 'us', 'mx', 'ar'],
  },
  {
    id: 'hash-explorer',
    group: 'payments',
    name: 'Hash Explorer',
    description: 'Inspect transactions across chains and trace deposits or withdrawals.',
    Icon: Hash,
    accent: { from: '#22d3ee', to: '#0891b2', glow: 'rgba(34, 211, 238, 0.45)' },
    image: '/tool-cards/hash-explorer.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com', 'us'],
  },
  {
    id: 'active-bets',
    group: 'bets',
    name: 'Active Bets',
    description: "Pull a user's live open bets across casino and sports in one view.",
    Icon: Dice5,
    accent: { from: '#a78bfa', to: '#6d28d9', glow: 'rgba(167, 139, 250, 0.45)' },
    image: '/tool-cards/active-bets.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com', 'us', 'mx', 'ar'],
  },
  {
    id: 'crash-bet-check',
    group: 'bets',
    name: 'Crash Bet Check',
    description: 'Verify Crash bet outcomes and reconcile provably-fair seeds in seconds.',
    Icon: Rocket,
    accent: { from: '#fb923c', to: '#dc2626', glow: 'rgba(251, 146, 60, 0.45)' },
    image: '/tool-cards/crash-bet-check.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com', 'us', 'mx', 'ar'],
  },
  {
    id: 'bonus-checker',
    group: 'bonuses',
    name: 'Bonus Checker',
    description: 'Audit active bonuses, wagering progress, and eligibility at a glance.',
    Icon: Gift,
    accent: { from: '#f472b6', to: '#be185d', glow: 'rgba(244, 114, 182, 0.45)' },
    image: '/tool-cards/bonus-checker.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com', 'us', 'mx', 'ar'],
  },
  {
    id: 'balance-calculator',
    group: 'account',
    name: 'Balance Calculator',
    description: 'Reconstruct historic account balances at any point in time.',
    Icon: Calculator,
    accent: { from: '#34d399', to: '#047857', glow: 'rgba(52, 211, 153, 0.45)' },
    image: '/tool-cards/balance-calculator.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
  {
    // Responsible Gambling (2026-10-26, Nikola's idea): may a self-excluded, mitigated or
    // product-excluded player ask to Return to Play, from when, and what to send them.
    // API twin: routes/rtpRoutes.js → services/rtpCheck.js (also Clara AI's rtp_check).
    id: 'rtp-check',
    group: 'account',
    name: 'RTP Check',
    description: 'Can an excluded player ask to return to play, from when, and what to send them.',
    Icon: Undo2,
    accent: { from: '#bef264', to: '#4d7c0f', glow: 'rgba(163, 230, 53, 0.45)' },
    image: '/tool-cards/rtp-check.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
  {
    id: 'bet-archive-search',
    group: 'bets',
    name: 'Bet Archive',
    description: "Search a user's full bet history and pull archived bets by day.",
    Icon: Archive,
    accent: { from: '#f59e0b', to: '#b45309', glow: 'rgba(245, 158, 11, 0.45)' },
    image: '/tool-cards/bet-archive-search.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
  {
    id: 'limit-lookup',
    group: 'account',
    name: 'Limit Lookup',
    description: 'Reconstruct deposit & gambling limit periods and which bets counted.',
    Icon: Gauge,
    accent: { from: '#06b6d4', to: '#0891b2', glow: 'rgba(6, 182, 212, 0.45)' },
    image: null,
    supportedMarkets: ['com'],
  },
  {
    // Filip, 2026-10-26: a player says on chat they were hacked — everything they hold in one place,
    // against the $1 line of the C-role procedure. API twin: routes/hackedAccountRoutes.js →
    // services/hackedBalance.js (stake.com only, like the SCS1 procedure it follows).
    id: 'hacked-accounts',
    group: 'account',
    name: 'Hacked Accounts',
    description: 'Everything a player holds in one place, and whether it calls for the C role.',
    Icon: ShieldAlert,
    accent: { from: '#f87171', to: '#b91c1c', glow: 'rgba(248, 113, 113, 0.45)' },
    image: '/tool-cards/hacked-accounts.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
  {
    id: 'affiliate-commission',
    group: 'payments',
    name: 'Affiliate Commission',
    description: "Convert an affiliate's FIAT commission to crypto with rule checks.",
    Icon: ArrowLeftRight,
    accent: { from: '#e879f9', to: '#a21caf', glow: 'rgba(232, 121, 249, 0.45)' },
    image: '/tool-cards/affiliate-commission.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
  {
    id: 'birthday-bonus',
    group: 'bonuses',
    name: 'Birthday Bonus',
    description: 'Check birthday-bonus eligibility and credit it per policy, or get the macro.',
    Icon: Cake,
    accent: { from: '#f472b6', to: '#be185d', glow: 'rgba(244, 114, 182, 0.45)' },
    image: '/tool-cards/birthday-bonus.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
  {
    id: 'welcome-offer-checker',
    group: 'bonuses',
    name: 'Welcome Offer Checker',
    description: 'Check affiliate welcome-offer eligibility and search the synced deal sheet.',
    Icon: Ticket,
    accent: { from: '#4ade80', to: '#15803d', glow: 'rgba(74, 222, 128, 0.45)' },
    image: '/tool-cards/welcome-offer-checker.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
  {
    // CRM team (Filip, 2026-09-22): which flag-progression ("ladder") bonuses a
    // player's lifetime wager owes them and which never reached the ACP. The
    // ladder sheet covers .com and .us; every market is selectable because an
    // account Optimove does not know simply ends the check (no table needed).
    id: 'ladder-checker',
    group: 'bonuses',
    name: 'Ladder Checker',
    description: 'Compare a player’s lifetime wager against the ladder bonuses and list the codes never credited.',
    Icon: Flag,
    accent: { from: '#fbbf24', to: '#b45309', glow: 'rgba(251, 191, 36, 0.45)' },
    image: null,
    supportedMarkets: ['com', 'us', 'mx', 'ar'],
  },
  {
    id: 'optimove',
    group: 'utilities',
    name: 'Optimove/Insider',
    description: 'Customer 360 across both CRMs: campaign history, email previews, consent.',
    Icon: Send,
    accent: { from: '#60a5fa', to: '#1d4ed8', glow: 'rgba(96, 165, 250, 0.45)' },
    image: '/tool-cards/optimove.webp',
    imageClass: 'object-top',
    // Optimove serves com/us; Insider has a panel per market, Moon included.
    supportedMarkets: ['com', 'us', 'mx', 'moon'],
  },
  {
    id: 'quick-links',
    group: 'utilities',
    name: 'Quick Links',
    description: 'A curated jump bar to every internal dashboard, tracker, and console.',
    Icon: Zap,
    accent: { from: '#facc15', to: '#a16207', glow: 'rgba(250, 204, 21, 0.45)' },
    image: '/tool-cards/quick-links.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
  {
    id: 'time-converter',
    group: 'utilities',
    name: 'Time Converter',
    description: 'Live world clocks and instant conversion across your saved time zones.',
    Icon: Clock,
    accent: { from: '#818cf8', to: '#3730a3', glow: 'rgba(129, 140, 248, 0.45)' },
    image: '/tool-cards/time-converter.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com', 'us', 'mx', 'ar'],
  },
  {
    id: 'countries-restrictions',
    group: 'utilities',
    name: 'Countries',
    description: 'Look up jurisdiction rules, restricted products, and policy notes.',
    Icon: Globe,
    accent: { from: '#2dd4bf', to: '#0f766e', glow: 'rgba(45, 212, 191, 0.45)' },
    image: '/tool-cards/countries-restrictions.webp',
    imageClass: 'object-top',
    supportedMarkets: ['com'],
  },
];

/**
 * Sections of the Tools rail, in display order. Every non-pinned tool names
 * one of these in its `group`; a section only renders when the user can see
 * at least one of its tools.
 */
export const TOOL_GROUPS = [
  { id: 'account', label: 'Account' },
  { id: 'payments', label: 'Payments' },
  { id: 'bets', label: 'Bets' },
  { id: 'bonuses', label: 'Bonuses' },
  { id: 'utilities', label: 'Utilities' },
];

/**
 * Who sees which tool — the ONE rule for the dashboard, the rail and the quick
 * search. Two tools are role-restricted (RESTRICTED_TOOL_ROLES); everything else is open to
 * every role, and a per-user `pagePermissions.tools.subPages[id] === false`
 * hides a tool for that one person. The two bonus senders are deliberately
 * not restricted: sending is gated by the per-user daily limit the audit team
 * grants, not by a role.
 */
const ADMIN_ROLES = ['admin', 'developer'];
// Restricted tools and who may see each. Ask Clara AI is NOT here any more: it opened to every
// role on 2026-09-23 (Filip) — until then admin/developer + the 'ai-tester' role (2026-09-19);
// the API twin routes/claraAiRoutes.js dropped its role gate the same day. The Welcome Offer
// Checker opened to every role on 2026-10-09 (Filip) — its APIs (/api/affiliate-deals, the
// relay, Clara AI's welcome_offer_check) need only a login.
export const RESTRICTED_TOOL_ROLES = {
  'limit-lookup': ADMIN_ROLES,
  // 'crm' (Filip, 2026-09-22) is admin-equal on ONE tool — the Ladder Checker — because the
  // thresholds behind the ladder codes are internal. Backend twin: routes/ladderRoutes.js
  // LADDER_ROLES; route guard: App.js /tools/ladder-checker; sidebar: navConfig.js ROLE_ACCESS['crm'].
  'ladder-checker': [...ADMIN_ROLES, 'crm'],
};
export const RESTRICTED_TOOL_IDS = Object.keys(RESTRICTED_TOOL_ROLES);

// Trainees (Position "Trainee" on QA Manager) don't use Clara AI (Filip, 2026-11-03). The tool
// stays in their list; opening it — the page, the /acp dock tab, a list's Quick AI — shows this
// instead. GET /api/auth/profile sets user.claraAiBlocked; the API refuses them with the same
// words (clarab/services/claraAi/traineeGate.js).
export const CLARA_TRAINEE_NOTICE = [
  'Clara AI is not available for trainees at the moment.',
  'If you are no longer in a trainee position, contact your shift leader.',
];

export function isToolVisible(user, tool) {
  const allowedRoles = RESTRICTED_TOOL_ROLES[tool.id];
  if (allowedRoles && !allowedRoles.includes(user?.role)) return false;
  const perms = user?.pagePermissions?.tools?.subPages;
  return perms ? perms[tool.id] !== false : true;
}

/** The tools this user may open, in registry order. */
export function visibleToolsFor(user) {
  return TOOLS.filter((tool) => isToolVisible(user, tool));
}

/**
 * The rail's shape for this user: pinned tools first (as a section with no
 * label), then each TOOL_GROUPS section that still has something in it.
 *   [{ id: 'pinned', label: null, tools }, { id: 'account', label: 'Account', tools }, …]
 */
export function groupedToolsFor(user) {
  const visible = visibleToolsFor(user);
  const sections = [];
  const pinned = visible.filter((tool) => tool.pinned);
  if (pinned.length) sections.push({ id: 'pinned', label: null, tools: pinned });
  TOOL_GROUPS.forEach((group) => {
    const tools = visible.filter((tool) => tool.group === group.id);
    if (tools.length) sections.push({ id: group.id, label: group.label, tools });
  });
  return sections;
}

export default TOOLS;
